import { createSelector, createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";

const usersAdapter = createEntityAdapter({});

const initialState = usersAdapter.getInitialState();

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query({
      query: () => ({
        url: "/users",
        validateStatus: (response, result) => {
          return response.status === 200 && !result.isError;
        },
      }),
      transformResponse: (responseData) => {
        const loadedUsers = responseData.map((user) => {
          user.id = user._id;
          return user;
        });
        return usersAdapter.upsertMany(initialState, loadedUsers);
      },

      providesTags: (result, error, arg) => {
        if (result?.ids) {
          return [
            { type: "User", id: "LIST" },
            ...result.ids.map((id) => ({ type: "User", id })),
          ];
        } else return [{ type: "User", id: "LIST" }];
      },
    }),
    registerUser: builder.mutation({
      query: (initialUserData) => ({
        url: "/users/register",
        method: "POST",
        body: {
          ...initialUserData,
        },
      }),
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),
    addNewUser: builder.mutation({
      query: (initialUserData) => ({
        url: "/users",
        method: "POST",
        body: {
          ...initialUserData,
        },
      }),
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),
    updateUser: builder.mutation({
      query: (initialUserData) => ({
        url: "/users",
        method: "PATCH",
        body: {
          ...initialUserData,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "User", id: arg.id }],
    }),
    deleteUser: builder.mutation({
      query: ({ id }) => ({
        url: `/users`,
        method: "DELETE",
        body: { id },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "User", id: arg.id }],
    }),
    getUserById: builder.query({
      query: (id) => ({
        url: `/users/${id}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useRegisterUserMutation,
  useAddNewUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
} = usersApiSlice;

// returns the query result object
export const selectUsersResult =
  usersApiSlice.endpoints.getUsers.select("usersList");

// creates memoized selector with options
export const selectUsers = (userId) =>
  createSelector(selectUsersResult, (usersResult) => {
    if (userId) {
      let user = null;
      const { data } = usersResult;

      for (const key in data?.entities) {
        if (data?.entities[key].id === userId) {
          user = data?.entities[key];
          break;
        }
      }

      return {
        data: user,
        isLoading: usersResult.isLoading,
        isSuccess: user !== null && usersResult.isSuccess,
        isError:
          usersResult.isError || (user === null && usersResult.isSuccess),
        error: usersResult.error || (user === null && "User not found"), // Include error from the query result object
      };
    } else {
      return {
        data: Object.values(usersResult.data?.entities || {}), // normalized array with entities
        isLoading: usersResult.isLoading,
        isSuccess: usersResult.isSuccess,
        isError: usersResult.isError,
        error: usersResult.error, // Include error from the query result object
      };
    }
  });

// creates memoized selector
const selectUsersData = createSelector(
  selectUsersResult,
  (usersResult) => usersResult.data
);

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
  selectAll: selectAllUsers,
  selectById: selectUserById,
  selectIds: selectUserIds,
  // Pass in a selector that returns the users slice of state
} = usersAdapter.getSelectors(
  (state) => selectUsersData(state) ?? initialState
);

// Create a selector to get the user by phone number
export const selectUserByPhone = createSelector(
  [selectAllUsers, (state, phoneNumber) => phoneNumber],
  (users, phoneNumber) => users.find((user) => user.phone === phoneNumber)
);
